import * as datadogRum from 'lib/telemetry/datadogRum';

type WsMonitorMetadata = Record<string, unknown>;

export class WsMonitor {
    private sessionStaleEvents = 0;
    private sessionReconnects = 0;
    private connectionStartedAt: number | null = null;

    private getContext(metadata: WsMonitorMetadata): WsMonitorMetadata {
        const connection =
            typeof navigator === 'undefined'
                ? undefined
                : (navigator as Navigator & { connection?: { effectiveType?: string } }).connection;
        return {
            ...metadata,
            timestamp: Date.now(),
            clientLabel: this.clientLabel,
            sessionStaleEvents: this.sessionStaleEvents,
            sessionReconnects: this.sessionReconnects,
            visibilityState: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
            effectiveType: connection?.effectiveType ?? 'unknown',
        };
    }

    // Per-channel last message tracking
    private channelLastMessageAt: Map<string, number> = new Map();

    constructor(private readonly clientLabel: string) {}

    track(event: string, metadata: WsMonitorMetadata = {}): void {
        const enriched = this.getContext(metadata);

        try {
            datadogRum.addAction(event, enriched);
        } catch {
            // Datadog may not be initialized yet
        }

        console.warn(`[WS_MONITOR ${this.clientLabel}] ${event}`, JSON.stringify(enriched));
    }

    trackDuration(name: string, startedAt: number, metadata: WsMonitorMetadata = {}): void {
        try {
            datadogRum.addDurationVital(name, {
                startTime: datadogRum.epochFromPerfNow(startedAt),
                duration: performance.now() - startedAt,
                context: this.getContext(metadata),
            });
        } catch {
            // Monitoring must never interrupt message delivery.
        }
    }

    onChannelMessage(channel: string): void {
        this.channelLastMessageAt.set(channel, Date.now());
    }

    /**
     * Get staleness info for all tracked channels.
     * Returns ms since last message for each channel.
     */
    getChannelStaleness(): Record<string, number | null> {
        const now = Date.now();
        const result: Record<string, number | null> = {};
        for (const [channel, lastAt] of this.channelLastMessageAt) {
            result[channel] = now - lastAt;
        }
        return result;
    }

    onConnect(): void {
        if (this.connectionStartedAt !== null) {
            this.sessionReconnects++;
            this.track('ws_reconnect');
        }
        this.connectionStartedAt = Date.now();
        this.track('ws_connected', {
            reconnectAttempt: this.sessionReconnects,
        });
    }

    onDisconnect(code?: number, reason?: string): void {
        const duration = this.connectionStartedAt !== null ? Date.now() - this.connectionStartedAt : undefined;
        this.track('ws_disconnected', {
            code,
            reason,
            connectionDuration: duration,
            channelStaleness: this.getChannelStaleness(),
        });
    }

    onStaleDetected(timeSinceLastMessage: number, subscriptionCount: number): void {
        this.sessionStaleEvents++;
        this.track('ws_stale_detected', {
            timeSinceLastMessage,
            subscriptionCount,
            channelStaleness: this.getChannelStaleness(),
        });
    }

    onStaleRecovery(): void {
        this.track('ws_stale_recovery');
    }

    onSubscriptionTimeout(): void {
        this.track('ws_subscription_timeout');
    }

    onError(message: string): void {
        this.track('ws_error', { reason: message });
    }

    /**
     * Reset per-channel tracking on reconnect.
     */
    resetChannelTracking(): void {
        this.channelLastMessageAt.clear();
    }

    getConnectionDuration(): number | null {
        return this.connectionStartedAt !== null ? Date.now() - this.connectionStartedAt : null;
    }

    getSessionStats(): { staleEvents: number; reconnects: number } {
        return {
            staleEvents: this.sessionStaleEvents,
            reconnects: this.sessionReconnects,
        };
    }
}
